/**
 * Camada `local_specialty`: separa PROVA DE CATEGORIA de PROVA DE PRODUTO.
 *
 * - prova de categoria → o local é de doçaria (basta para entrar numa rota
 *   com foco `sweets`);
 * - prova de produto → evidência específica daquele produto naquele local
 *   (ligação curada verificada, nome do estabelecimento, ou menção em
 *   avaliações/descrição). SÓ isto autoriza `specialtyLabel`.
 *
 * Nunca se infere o produto a partir do doce emblemático da cidade.
 */

import { stopMatchesFocus, type FocusCategory } from "./exclusive-category";
import {
  catalogSpecialtyForTerm,
  foldSpecialty,
  matchesCuratedVenue,
  type CuratedSpecialty,
} from "./local-specialty-catalog";

export type SpecialtyEvidenceLevel = "product" | "category" | "none";

export type SpecialtyEvidence = {
  level: SpecialtyEvidenceLevel;
  /** Só existe com prova de produto. */
  label?: string;
  /** Razão legível da prova. */
  reason?: string;
  /** true quando havia um alvo específico e este local não o confirma. */
  alternative?: boolean;
};

export type SpecialtyStopInput = {
  name?: string | null;
  category?: string | null;
  blurb?: string | null;
  placeId?: string | null;
  lat?: number | null;
  lng?: number | null;
  countryCode?: string | null;
  /** Texto adicional real (avaliações, descrição editorial). */
  evidenceText?: string | null;
};

export type SpecialtyEvaluationOptions = {
  focus: FocusCategory;
  /** Produto/alvo pedido pelo utilizador, se existir. */
  targetProduct?: string | null;
  /** Entrada curada do produto pedido, se existir. */
  catalogEntry?: CuratedSpecialty | null;
  language?: string;
  countryCode?: string | null;
};

const REASONS: Record<string, Record<string, string>> = {
  curated: {
    pt: "ligação curada verificada",
    en: "verified curated link",
    es: "vínculo curado verificado",
    fr: "lien vérifié du catalogue",
    it: "collegamento curato verificato",
    de: "geprüfte kuratierte Verknüpfung",
  },
  name: {
    pt: "nome do estabelecimento",
    en: "venue name",
    es: "nombre del establecimiento",
    fr: "nom de l'établissement",
    it: "nome del locale",
    de: "Name des Lokals",
  },
  mentions: {
    pt: "referido em avaliações",
    en: "mentioned in reviews",
    es: "mencionado en reseñas",
    fr: "mentionné dans les avis",
    it: "citato nelle recensioni",
    de: "in Bewertungen erwähnt",
  },
  category: {
    pt: "estabelecimento de doçaria",
    en: "pastry venue",
    es: "establecimiento de repostería",
    fr: "établissement de pâtisserie",
    it: "pasticceria",
    de: "Konditorei",
  },
};

const lang2 = (language?: string) => (language || "pt").slice(0, 2).toLowerCase();
const reason = (key: keyof typeof REASONS | string, language?: string) =>
  REASONS[key]?.[lang2(language)] ?? REASONS[key]?.en ?? "";

/** Termos que provam o produto num texto real do local. */
function productTerms(
  targetProduct?: string | null,
  catalogEntry?: CuratedSpecialty | null,
): string[] {
  const terms = new Set<string>();
  if (targetProduct) terms.add(foldSpecialty(targetProduct));
  if (catalogEntry) {
    terms.add(foldSpecialty(catalogEntry.product));
    for (const t of [...catalogEntry.aliases, ...catalogEntry.evidenceTerms]) {
      terms.add(foldSpecialty(t));
    }
  }
  return [...terms].filter((t) => t.length >= 4);
}

/**
 * Avalia a prova de um local. NUNCA devolve `label` sem prova de produto.
 */
export function evaluateSpecialtyEvidence(
  stop: SpecialtyStopInput,
  opts: SpecialtyEvaluationOptions,
): SpecialtyEvidence {
  const catalogEntry =
    opts.catalogEntry ??
    (opts.targetProduct ? catalogSpecialtyForTerm(opts.targetProduct, opts.countryCode) : null);
  const hasCategoryProof = stopMatchesFocus(opts.focus, {
    name: stop.name ?? undefined,
    category: stop.category ?? undefined,
  });
  const wantsProduct = !!(opts.targetProduct || catalogEntry);
  const productLabel = catalogEntry?.product ?? opts.targetProduct ?? undefined;

  if (wantsProduct) {
    // 1. Ligação curada: provider + placeId + país, coordenadas em fallback.
    const curated = catalogEntry?.venues.find((link) =>
      matchesCuratedVenue(link, {
        placeId: stop.placeId ?? null,
        lat: stop.lat ?? null,
        lng: stop.lng ?? null,
        countryCode: stop.countryCode ?? opts.countryCode ?? null,
      }),
    );
    if (curated) {
      return {
        level: "product",
        label: productLabel,
        reason: `${reason("curated", opts.language)} — ${curated.source}`,
      };
    }
    const terms = productTerms(opts.targetProduct, catalogEntry);
    const name = foldSpecialty(stop.name ?? "");
    if (terms.some((t) => name.includes(t))) {
      return { level: "product", label: productLabel, reason: reason("name", opts.language) };
    }
    const text = foldSpecialty(`${stop.blurb ?? ""} ${stop.evidenceText ?? ""}`);
    if (text && terms.some((t) => text.includes(t))) {
      return { level: "product", label: productLabel, reason: reason("mentions", opts.language) };
    }
    // Sem prova do produto: nunca etiquetar. Fica prova de categoria, marcada
    // como alternativa quando havia um alvo específico.
    if (hasCategoryProof) {
      return {
        level: "category",
        reason: reason("category", opts.language),
        alternative: true,
      };
    }
    return { level: "none" };
  }

  if (hasCategoryProof) {
    return { level: "category", reason: reason("category", opts.language) };
  }
  return { level: "none" };
}

const CARD_LABELS: Record<string, { product: string; category: string; unconfirmed: string; alternative: string }> = {
  pt: {
    product: "Especialidade a provar",
    category: "Categoria",
    unconfirmed: "Especialidade regional não confirmada",
    alternative: "Alternativa",
  },
  en: {
    product: "Specialty to taste",
    category: "Category",
    unconfirmed: "Regional specialty not confirmed",
    alternative: "Alternative",
  },
  es: {
    product: "Especialidad para probar",
    category: "Categoría",
    unconfirmed: "Especialidad regional no confirmada",
    alternative: "Alternativa",
  },
  fr: {
    product: "Spécialité à goûter",
    category: "Catégorie",
    unconfirmed: "Spécialité régionale non confirmée",
    alternative: "Alternative",
  },
  it: {
    product: "Specialità da provare",
    category: "Categoria",
    unconfirmed: "Specialità regionale non confermata",
    alternative: "Alternativa",
  },
  de: {
    product: "Spezialität zum Probieren",
    category: "Kategorie",
    unconfirmed: "Regionale Spezialität nicht bestätigt",
    alternative: "Alternative",
  },
};

/**
 * Linha a mostrar no cartão. Sem prova de produto, mostra a categoria ou o
 * aviso de não confirmação — nunca o doce emblemático da cidade.
 */
export function specialtyCardLine(
  evidence: SpecialtyEvidence,
  categoryLabel: string,
  language?: string,
): string | null {
  const labels = CARD_LABELS[lang2(language)] ?? CARD_LABELS.en;
  if (evidence.level === "product" && evidence.label) {
    return `${labels.product}: ${evidence.label}`;
  }
  if (evidence.alternative) {
    return `${labels.unconfirmed} · ${labels.alternative}`;
  }
  if (evidence.level === "category") {
    return `${labels.category}: ${categoryLabel}`;
  }
  return null;
}

/** Anota uma lista de paragens com a prova de especialidade. */
export function annotateStopsWithSpecialty<T extends SpecialtyStopInput>(
  stops: readonly T[],
  opts: SpecialtyEvaluationOptions,
): Array<T & { specialtyLabel?: string; specialtyEvidence?: string; specialtyEvidenceLevel: SpecialtyEvidenceLevel; specialtyAlternative?: boolean }> {
  return stops.map((stop) => {
    const evidence = evaluateSpecialtyEvidence(stop, opts);
    return {
      ...stop,
      specialtyLabel: evidence.label,
      specialtyEvidence: evidence.reason,
      specialtyEvidenceLevel: evidence.level,
      specialtyAlternative: evidence.alternative,
    };
  });
}
